"use client";

import { useStatsigClient } from "@statsig/react-bindings";
import { usePostHog } from "posthog-js/react";
import { createContext, useCallback, useContext, useRef } from "react";

export interface AppEventTypes {
	signin_click: { placement: "header" | "mobile_menu" };
	signup_click: {
		placement:
			| "header"
			| "mobile_menu"
			| "lp_hero"
			| "lp_cta"
			| "pricing_free_plan"
			| "pricing_cta"
			| "about_cta"
			| "blog_cta"
			| "blog_article_cta"
			| "reclaim_your_time";
	};
	select_plan_click: {
		plan: "plus" | "pro" | "ultra";
		frequency: "monthly" | "annually" | "yearly";
	};
	book_demo_click: undefined;
	watch_demo_click: undefined;
	send_application_click: undefined;
	continue_with_google_click: undefined;
	continue_with_outlook_click: undefined;
	checklist_onboarding_finished: undefined;
	checklist_onboarding_step_completed: {
		step: string;
	};
}

// Type for event keys
export type AppEventType = keyof AppEventTypes;

// This type ensures proper type checking for each event type's payload
// It makes the payload optional when the payload type is undefined
export type AppEvent = {
	[K in AppEventType]: AppEventTypes[K] extends undefined
		? { type: K; payload?: undefined }
		: { type: K; payload: AppEventTypes[K] };
}[AppEventType];

export type EventCallback = (event: AppEvent) => void;

interface AppEventsContextType {
	emit: (event: AppEvent) => void;
	on: (eventType: AppEventType, callback: EventCallback) => void;
	off: (eventType: AppEventType, callback: EventCallback) => void;
}

const AppEventsContext = createContext<AppEventsContextType | null>(null);

export function AppEventsProvider({ children }: React.PropsWithChildren) {
	const posthog = usePostHog();
	const { client: statsigClient } = useStatsigClient();

	const listeners = useRef<Record<AppEventType, EventCallback[]>>(
		{} as Record<AppEventType, EventCallback[]>,
	);

	const emit = useCallback(
		(event: AppEvent) => {
			// Log event in development environment
			if (process.env.NODE_ENV === "development") {
				console.info("App Event:", event.type, event.payload ?? "No payload");
			}

			// Capture event into PostHog
			posthog?.capture(event.type, event.payload || {});

			// Log event into Statsig
			// During testing phase we'd like to log only 2 events
			if (
				event.type === "continue_with_google_click" ||
				event.type === "signup_click"
			) {
				statsigClient.logEvent(event.type, undefined, event.payload);
			}

			// Call all listeners
			const eventListeners = listeners.current[event.type] ?? [];
			for (const callback of eventListeners) {
				callback(event);
			}
		},
		[posthog],
	);

	const on = useCallback((eventType: AppEventType, callback: EventCallback) => {
		listeners.current = {
			...listeners.current,
			[eventType]: [...(listeners.current[eventType] ?? []), callback],
		};
	}, []);

	const off = useCallback(
		(eventType: AppEventType, callback: EventCallback) => {
			listeners.current = {
				...listeners.current,
				[eventType]: (listeners.current[eventType] ?? []).filter(
					(cb) => cb !== callback,
				),
			};
		},
		[],
	);

	return (
		<AppEventsContext.Provider value={{ emit, on, off }}>
			{children}
		</AppEventsContext.Provider>
	);
}

export function useAppEvents(): AppEventsContextType {
	const context = useContext(AppEventsContext);

	if (!context) {
		throw new Error("useAppEvents must be used within an AppEventsProvider");
	}

	return context;
}
